import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { endpointOidcProviderQueryKeys, EndpointOIDCProviders, googleIdPQueryKeys } from '#api';
import type { Successful } from '#dn-types/api.ts';
import { makeMaxResourceQueryFn } from '#util/api.ts';
import type { GetEndpointOIDCProvidersResult } from './endpoint-oidc-providers.ts';
import { AuthorizationError } from './errors.ts';

export const maxEndppointOIDCProvidersQueryOptions = queryOptions({
  queryKey: endpointOidcProviderQueryKeys.maxList,
  queryFn: makeMaxResourceQueryFn(EndpointOIDCProviders.getEndpointOIDCProviders),
});

export function useSuspenseMaxEndpointOIDCProvidersQuery() {
  return useSuspenseQuery(maxEndppointOIDCProvidersQueryOptions);
}

export function useSuspenseEndpointOIDCProviderQuery(providerId: string) {
  const queryClient = useQueryClient();

  return useSuspenseQuery({
    queryKey: endpointOidcProviderQueryKeys.detail(providerId),
    queryFn: ({ signal }) => EndpointOIDCProviders.getEndpointOIDCProvider(providerId, { signal }),
    initialData: () => {
      const listQueryResults = queryClient.getQueriesData<
        Successful<GetEndpointOIDCProvidersResult | null> | undefined
      >({ queryKey: endpointOidcProviderQueryKeys.maxList });
      const provider = listQueryResults
        .flatMap((hit) => hit[1]?.data)
        .find((d) => {
          return d?.id === providerId;
        });

      // If we found a match, use it as the initial data for the query
      if (provider) {
        return { data: provider };
      }
    },
    initialDataUpdatedAt: () =>
      queryClient.getQueryState<Successful<GetEndpointOIDCProvidersResult | null> | undefined>(
        endpointOidcProviderQueryKeys.maxList
      )?.dataUpdatedAt,
  });
}

export function makeEndpointOIDCProviderQueryOptions(id: string) {
  return queryOptions({
    queryKey: endpointOidcProviderQueryKeys.detail(id),
    queryFn: ({ signal }) => EndpointOIDCProviders.getEndpointOIDCProvider(id, { signal }),
  });
}

export function useAddEndpointOIDCProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: EndpointOIDCProviders.addEndpointOIDCProvider,
    onSuccess: () => {
      // We could try to shove this in the list cache, but we don't know how to sort it
      void queryClient.invalidateQueries(maxEndppointOIDCProvidersQueryOptions);
      // Which providers are Google, and whether the directory connection is paused, follow the providers.
      void queryClient.invalidateQueries({ queryKey: googleIdPQueryKeys.all() });
    },
  });
}

export function useEditEndpointOIDCProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: EndpointOIDCProviders.editEndpointOIDCProvider,
    throwOnError: (error) => error instanceof AuthorizationError,
    onSuccess: (data, { id }) => {
      // Update the cached details query
      queryClient.setQueryData(endpointOidcProviderQueryKeys.detail(data.id), { data });
      // Update the list query cache.
      // This only works because the return type from the list and details endpoints are the same
      queryClient.setQueryData(maxEndppointOIDCProvidersQueryOptions.queryKey, (oldData) => {
        if (oldData) {
          return {
            ...oldData,
            data: oldData.data.map((p) => {
              return p.id === id ? data : p;
            }),
          };
        }
      });
      // Which providers are Google, and whether the directory connection is paused, follow the providers.
      void queryClient.invalidateQueries({ queryKey: googleIdPQueryKeys.all() });
    },
  });
}
